<template>
  <BaseModal :open="open" @update:open="updateOpen" dialog-classes="w-screen max-w-xl z-max">
    <div class="rounded-md bg-white px-4 py-6 lg:px-6">
      <p class="mb-4 text-2xl font-bold text-slate-900">Edit Item</p>
      <div v-if="allows.type">
        <FormSelectDropdown
          :options="allows.type"
          label="Education Type"
          :model-value="item.type"
          @update:model-value="updateItem('type', $event)"
          class="mb-4"
        />
      </div>
      <FormInput
        v-if="allows.primary && !typeIsGED"
        :label="allows.primary"
        :placeholder="allows.primary"
        :model-value="item.primary"
        @update:model-value="updateItem('primary', $event)"
        class="mb-4"
      />
      <FormDropdownSearchAsync v-if="allows.primaryList"
                               :model-value="item.primary"
                                @update:model-value="updateItem('primary', $event)"
                               allow-support-request
                               :support-request-type="TYPE_JOB_TITLE"
                               placeholder="Select an opportunity title"
                               :label="allows.primaryList"
                               :get-options="(params) => allows.getPrimaryList(params)"
                               class="mb-4 w-full flex-1"/>
      <FormInput
        v-if="allows.title"
        :label="allows.title"
        :placeholder="allows.title"
        :model-value="item.title"
        @update:model-value="updateItem('title', $event)"
        class="mb-4"
      />
      <FormInput
        v-if="allows.subtitle"
        :label="allows.subtitle"
        :placeholder="allows.subtitle"
        :model-value="item.subtitle"
        @update:model-value="updateItem('subtitle', $event)"
        class="mb-4"
      />
      <div v-if="allows.completion" class="mb-4 text-slate-600">
        <FormRadio
          v-for="completionLevel in allows.completion"
          :key="completionLevel"
          name="completion"
          class="mb-2"
          :model-value="item.completion"
          @update:modelValue="updateCompletion($event)"
          :option="completionLevel"
        />
      </div>
      <div
        v-if="allows.start"
        class="mb-4 flex flex-col justify-between gap-4 md:flex-row"
      >
        <FormDate
          disable-utc
          v-if="allows.start"
          :label="allows.start"
          :placeholder="allows.start"
          month-picker
          class="w-full date-picker-container"
          popoverPlacement="bottom"
          :model-value="itemStart"
          @update:modelValue="updateStart($event)"
        />
        <FormDate
          disable-utc
          v-if="allows.end"
          :label="allows.end + (itemIsCompletableButIsNotCompleted ? ' (Optional)' : '')"
          :placeholder="itemIsCompletableButIsNotCompleted ? 'Projected End' : 'End date'"
          month-picker
          class="w-full date-picker-container"
          popoverPlacement="bottom"
          :model-value="itemEnd"
          @update:modelValue="updateEnd($event)"
        />
      </div>
      <FormTextarea
        v-if="allows.description"
        :label="allows.description"
        :placeholder="allows.description"
        :model-value="item.description"
        :max-length="4096"
        @update:model-value="updateItem('description', $event)"
        :default-height="80"
      />
      <div v-if="allows.tenure && tenure" class="mb-6">
        <FormLabel label="Tenure"/>
        <p class="-mt-1">{{ tenure }}</p>
      </div>
      <div v-if="allows.rank">
        <FormSlider :label="allows.rank" :model-value="item?.rank" @update:model-value="updateItem('rank', $event)"/>
        <p class="my-4 text-center text-slate-600">
          {{ getRankMessage(item.rank) }}
        </p>
      </div>
      <FormCheckbox v-if="allows.honors" :model-value="item?.honors" @update:model-value="updateItem('honors', $event)">
        {{ allows.honors }}
      </FormCheckbox>
      <FormButton color="green" class="mx-auto mt-6 block max-w-fit" @click="updateOpen(false)">Done</FormButton>
    </div>
  </BaseModal>
</template>

<script setup>
import BaseModal from "./BaseModal.vue";
import FormButton from "./FormButton.vue";
import FormInput from "./FormInput.vue";
import FormSlider from "./FormSlider.vue";
import FormTextarea from "./FormTextarea.vue";
import FormCheckbox from "./FormCheckbox.vue";
import FormRadio from "./FormRadio.vue";
import FormDate from "./FormDate.vue";
import FormSelectDropdown from "./FormSelectDropdown.vue";
import FormDropdownSearchAsync from "./FormDropdownSearchAsync.vue";
import FormLabel from "./FormLabel.vue";
import {TYPE_JOB_TITLE} from "../constants/SupportRequests.js";
import {differenceBetweenDates, formatDaysReadable} from "../helpers/FormatDates.js";
import {getRankMessage} from "../helpers/CandidateFunctions.js";
import {computed} from "vue";

const emits = defineEmits(["update:item", "update:open"]);
const props = defineProps({
  open: Boolean,
  item: [Object, String],
  allows: Object,
});

const updateOpen = (value) => emits("update:open", value);

const updateItem = (key, value) => {
  const item = {...props.item};
  if (item.isNew) delete item.isNew;
  item[key] = value;
  emits("update:item", item);
};

const bulkUpdateItem = (values) => {
  const item = {...props.item};
  if (item.isNew) delete item.isNew;
  Object.keys(values).forEach(key => {
    item[key] = values[key];
  });
  emits("update:item", item);
};

const itemStart = computed(() => {
  if (!props.item?.start) return null;
  return new Date(props.item.start);
});

const itemEnd = computed(() => {
  if (!props.item?.end) return null;
  return new Date(props.item.end);
});

const typeIsGED = computed(() => {
  if (!props.item.type) return false;
  return props.item.type === "High School/GED";
});

const updateStart = (value) => {
  if (!props.item?.end || !value) updateItem("tenure", null);
  else updateItem("tenure", differenceBetweenDates(value, props.item.end, "years"));
  updateItem("start", value);
};

const updateEnd = (value) => {
  if (!props.item?.start || !value) updateItem("tenure", null);
  else updateItem("tenure", differenceBetweenDates(props.item.start, value, "years"));
  updateItem("end", value);
};

const itemIsCompletableButIsNotCompleted = computed(() => {
  if (!props.allows?.completion) return false;
  return ["Partially Completed", "Currently Attending", "Currently Working Here"].includes(props.item?.completion);
});

const tenure = computed(() => {
  if (!props.item?.start) return null;
  if (!props.item?.end) return formatDaysReadable(differenceBetweenDates(props.item.start, new Date(), "days"));
  return formatDaysReadable(differenceBetweenDates(props.item.start, props.item.end, "days"));
});

const updateCompletion = (completion) => {
  if (completion === "Graduated") toast.info("Official graduation may be verified before hire");

  bulkUpdateItem({
    ...props.item,
    completion,
    ...itemIsCompletableButIsNotCompleted.value && {
      end: null,
      tenure: null,
    },
  });
};
</script>

<style scoped>
@media (min-width: 768px) {
  .date-picker-container {
    max-width: calc(50% - 0.5rem);
  }
}
</style>
